// dependencies
import { z } from "zod";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getMatchedRouteKeyValue } from "yarrr";
import CreatableSelect from "react-select/creatable";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// components
import Grid from "../../../../../Components/Grid";
import Spacer from "../../../../../Components/Spacer";
import Button from "../../../../../Components/Button";
import Text from "../../../../../Components/Inputs/Text";
import Radio from "../../../../../Components/Inputs/Radio";
import Textarea from "../../../../../Components/Inputs/Textarea";
import { getCurrentEnvironment } from "../../../../../Selectors/app";
import useForm from "../../../../../Hooks/useForm";
import { updateCompanyFeatureFlag } from "../../../../../Api/services/company";
import { useCompanyFeatureFlagAuditLog, useCompanyFeatureFlag, useFeatureFlag } from "../../../../../Api/hooks";

// styles
import css from "./EditFeatureFlag.styl";

const CompanyFeatureFlags = (props) => {
	const { companyID } = props;
	const [isLoaded, setIsLoaded] = useState(false);
	const queryClient = useQueryClient();
	const slug = useSelector((state) => getMatchedRouteKeyValue(state, "viewID", null));

	const env = useSelector(getCurrentEnvironment);
	const featureFlagQuery = useFeatureFlag(env.name, slug);
	const featureFlagRequest = useCompanyFeatureFlag(env.name, companyID, slug);
	const auditLogRequest = useCompanyFeatureFlagAuditLog(env.name, companyID, slug);

	const data = featureFlagRequest.data;
	const auditLogs = auditLogRequest?.data?.logs || [];

	const { state, setInitialState, updateForm } = useForm({
		schema: {
			nextValue: z.string(),
		},
	});

	// Mutations
	const mutation = useMutation({
		mutationFn: updateCompanyFeatureFlag,
		onSuccess: () => {
			// Invalidate and refetch
			queryClient.invalidateQueries({ queryKey: ["company-feature-flags", env, companyID] });
			queryClient.invalidateQueries({ queryKey: ["company-feature-flag-audit-log", env, companyID, slug] });
			auditLogRequest.refetch();
			featureFlagRequest.refetch();
		},
	});

	useEffect(() => {
		if (data) {
			setInitialState({
				nextValue: data.value.toString(),
			});
			setIsLoaded(true);
		}
	});

	if (featureFlagRequest.isLoading || !isLoaded) {
		return <div>Loading...</div>;
	}

	const nextValue = state.nextValue || "";

	const handleUpdate = async () => {
		await mutation.mutateAsync({
			environment: env.name,
			companyId: companyID,
			slug: slug,
			data: {
				value: nextValue,
			},
		});

		featureFlagQuery.refetch();
	};

	return (
		<div style={{ paddingTop: 16 }}>
			<div className={css.name}>{data.name}</div>
			<div className={css.description}>{data.description}</div>

			<Spacer size={2} />

			<div className={css.dataBox}>
				<Grid columns={["40px", "1fr"]}>
					<div className={css.label}>Tags</div>
					<div className={css.value}>
						{data.tags.map((tag) => {
							return (
								<div key={tag} className={css.tag}>
									{tag}
								</div>
							);
						})}
					</div>
				</Grid>
			</div>

			<div className={css.dataBox}>
				<Grid columns={["40px", "1fr"]}>
					<div className={css.label}>Type</div>
					<div className={css.value}>{data.type}</div>
				</Grid>
			</div>

			<Spacer size={3} />

			<div className={css.dataBox}>
				<div className={css.largeLabel}>Payload</div>
				<Spacer size={1} />
				<div className={css.payload}>{data.value.toString()}</div>
			</div>

			<Spacer size={3} />

			<div className={css.dataBox}>
				<div className={css.largeLabel}>Update Payload</div>
				<Spacer size={1} />

				{data.type === "boolean" ? (
					<>
						<Radio
							value={nextValue}
							options={[
								{ id: "true", label: "Visible" },
								{ id: "false", label: "Hidden" },
							]}
							onChange={(val) => updateForm({ nextValue: val })}
						/>
						<Spacer size={1} />
						<Grid columns={["100px", "1fr"]}>
							<Button onClick={handleUpdate}>Update</Button>
							<div />
						</Grid>
					</>
				) : null}

				{data.type === "number" ? (
					<>
						<Text value={nextValue} onChange={(val) => updateForm({ nextValue: val })} />
						<Spacer size={1} />
						<Grid columns={["100px", "1fr"]}>
							<Button onClick={handleUpdate}>Update</Button>
							<div />
						</Grid>
					</>
				) : null}

				{data.type === "string" ? (
					<>
						{/* <Text value={nextValue} onChange={(val) => updateForm({ nextValue: val })} /> */}
						<CreatableSelect
							isClearable={true}
							isSearchable={true}
							onChange={(update, action) => {
								if (action.action === "create-option") {
									updateForm({ nextValue: update.value });
								} else if (action.action === "select-option") {
									updateForm({ nextValue: update.id });
								} else if (action.action === "clear") {
									updateForm({ nextValue: "" });
								}
							}}
							defaultValue={{ value: nextValue, label: nextValue }}
							options={(featureFlagQuery?.data?.featureFlag?.options?.elements ?? []).map((element) => ({
								id: element,
								label: element,
							}))}
						/>
						<Spacer size={1} />
						<Grid columns={["100px", "1fr"]}>
							<Button onClick={handleUpdate}>Update</Button>
							<div />
						</Grid>
					</>
				) : null}

				{data.type === "json" ? (
					<>
						<Textarea value={nextValue} onChange={(val) => updateForm({ nextValue: val })} />
						<Spacer size={1} />
						<Grid columns={["100px", "1fr"]}>
							<Button onClick={handleUpdate}>Update</Button>
							<div />
						</Grid>
					</>
				) : null}
			</div>

			<Spacer size={3} />

			<div className={css.dataBox}>
				<div className={css.largeLabel}>History</div>
				{auditLogs.map((log) => {
					const action =
						log._id === "created" ? "created the flag and initialized to" : "updated the flag to be";

					return (
						<div key={log._id} className={css.auditLog}>
							<span className={css.bold}>{log.user.user}</span> {action}{" "}
							<span className={css.highlight}>{log.data.value.toString()}</span> on{" "}
							{format(new Date(log.created_at), "MMM d, yyyy hh:mm a")}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default CompanyFeatureFlags;
